import { APP_CONFIG, DragService, DragSource, IAppConfig } from 'rg-utils';
import { Directive, EventEmitter, HostBinding, HostListener, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { DropSortableEvent } from '../models/drop.sortable.event';

@Directive({
    // tslint:disable-next-line:directive-selector
    selector: '[dragSortable]'
})
export class DragSortableDirective implements OnInit, OnDestroy {
    @Input() item;
    @Input() itemIndex: number;
    @Input() sortableElSelector: string;
    @Output() dropSortable: EventEmitter<DropSortableEvent> = new EventEmitter();

    private ngUnsubscribe: Subject<void> = new Subject<void>();

    @HostBinding('draggable') get draggable() { return true; }
    @HostBinding('class.collapsed') collapsed = false;
    @HostBinding('class.dragging') dragging = false;
    @HostBinding('class.safari-specific') isSafari = this.config.isSafari;
    @HostBinding('class.grabbable') grabbing = true;

    @HostListener('dragstart', ['$event']) onDragStart(event) {
        this.grabbing = false;
        this.dragging = true;
        this.dragService.dragSource$.next({ element: event.target, index: this.itemIndex });
        if (this.config.isSafari) {
            // Safari doesn't allow this logic to be executed in dragstart event handler.
            setTimeout(() => {
                this.dragService.collapse$.next(true);
            }, 0);
        } else {
            this.dragService.collapse$.next(true);
        }

        // IE allowes only text type to be set.
        try {
            event.dataTransfer.setData('application/json', JSON.stringify(this.item));
        } catch (e) {
            event.dataTransfer.setData('text', JSON.stringify(this.item));
        }
    }

    @HostListener('drag', ['$event']) onDrag(event) {
        // do nothing while dragging item. Apply here effects while dragging
    }

    @HostListener('dragenter', ['$event']) onDragEnter(event) {
        this.dragService.dragSource$
            .pipe(
                takeWhile(dragSource => Boolean(dragSource)),
                take(1)
            )
            .subscribe((dragSource: DragSource) => {
                const dragEl = dragSource.element;
                const dragElIndex = dragSource.index;
                const textNodeType = 3;
                const target = event.target.nodeType === textNodeType ?
                    event.target.parentNode :
                    event.target;
                const sortableEl = target.closest(this.sortableElSelector);

                if (dragEl !== sortableEl) {
                    if (isBefore(dragEl, sortableEl)) {
                        sortableEl.parentNode.insertBefore(dragEl, sortableEl);
                        this.dragService.dragSource$.next(Object.assign({}, dragSource, {
                            index: dragElIndex - 1
                        }));
                    } else {
                        sortableEl.parentNode.insertBefore(dragEl, sortableEl.nextSibling);
                        this.dragService.dragSource$.next(Object.assign({}, dragSource, {
                            index: dragElIndex + 1
                        }));
                    }
                }
            });

        event.preventDefault();

        function isBefore(firstNode, secondNode): boolean {
            if (firstNode.parentNode === secondNode.parentNode) {
                for (let cur = firstNode; cur; cur = cur.previousSibling) {
                    if (cur === secondNode) {
                        return true;
                    }
                }
            }
            return false;
        }
    }

    @HostListener('dragover', ['$event']) onDragOver(event) {
        // Prevent default to allow drop.
        event.preventDefault();
    }

    @HostListener('dragend', ['$event']) onDragEnd(event) {
        this.grabbing = true;
        this.dragging = false;
        this.dragService.dragSource$.next(null);
        this.dragService.collapse$.next(false);
    }

    @HostListener('drop', ['$event']) onDrop(event) {
        this.dragService.dragSource$
            .pipe(
                takeWhile(dragSource => Boolean(dragSource)),
                take(1)
            )
            .subscribe((dragSource: DragSource) => {
                let data;
                console.log('drop event: ', event);
                // IE allowes only text type to be set.
                try {
                    data = JSON.parse(event.dataTransfer.getData('application/json'));
                } catch (e) {
                    data = JSON.parse(event.dataTransfer.getData('text'));
                }
                this.dropSortable.emit({ data, newIndex: dragSource.index });
            });
    }

    constructor(
        @Inject(APP_CONFIG) private config: IAppConfig,
        private dragService: DragService) { }

    ngOnInit() {
        this.dragService.collapse$
            .pipe(
                takeUntil(this.ngUnsubscribe),
            )
            .subscribe((collapsedState: boolean) => {
                this.collapsed = collapsedState;
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
